import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import type { Components } from 'react-markdown'
import type { ReactNode } from 'react'
import styles from './MarkdownRenderer.module.css'

interface MarkdownRendererProps {
  content: string
}

/** SOC section keywords that receive a distinct heading treatment. */
const SECTION_KEYWORDS = [
  'ASSESSMENT',
  'INCIDENT',
  'IDENTITY',
  'ENDPOINT',
  'CORRELATION',
  'RISK',
  'RECOMMENDED NEXT INVESTIGATION',
  'RESPONSE CONSIDERATIONS',
  'EVIDENCE',
  'EVIDENCE GAPS',
]

function isSocSectionHeading(text: string): boolean {
  const normalized = text.trim().toUpperCase()
  return SECTION_KEYWORDS.some((keyword) => normalized === keyword || normalized.startsWith(`${keyword}:`))
}

function headingText(children: ReactNode): string {
  return Array.isArray(children) ? children.map(String).join('') : String(children ?? '')
}

const components: Components = {
  h1: ({ children }) => <h1 className={styles.h1}>{children}</h1>,
  h2: ({ children }) => {
    const text = headingText(children)
    return (
      <h2 className={`${styles.h2} ${isSocSectionHeading(text) ? styles.sectionHeading : ''}`}>
        {children}
      </h2>
    )
  },
  h3: ({ children }) => {
    const text = headingText(children)
    return (
      <h3 className={`${styles.h3} ${isSocSectionHeading(text) ? styles.sectionHeading : ''}`}>
        {children}
      </h3>
    )
  },
  p: ({ children }) => <p className={styles.paragraph}>{children}</p>,
  ul: ({ children }) => <ul className={styles.list}>{children}</ul>,
  ol: ({ children }) => <ol className={styles.list}>{children}</ol>,
  li: ({ children }) => <li className={styles.listItem}>{children}</li>,
  a: ({ children, href }) => (
    <a className={styles.link} href={href} target="_blank" rel="noreferrer noopener">
      {children}
    </a>
  ),
  code: ({ children, className }) => {
    const isBlock = Boolean(className)
    if (isBlock) {
      return <code className={`${styles.codeBlock} ${className ?? ''}`}>{children}</code>
    }
    return <code className={styles.inlineCode}>{children}</code>
  },
  pre: ({ children }) => <pre className={styles.pre}>{children}</pre>,
  blockquote: ({ children }) => <blockquote className={styles.blockquote}>{children}</blockquote>,
  table: ({ children }) => (
    <div className={styles.tableWrap}>
      <table className={styles.table}>{children}</table>
    </div>
  ),
  strong: ({ children }) => <strong className={styles.strong}>{children}</strong>,
  hr: () => <hr className={styles.hr} />,
}

/**
 * Renders agent markdown content safely (no dangerouslySetInnerHTML).
 * Enhances readability of structured SOC responses without altering or
 * reinterpreting the agent's actual content.
 */
export function MarkdownRenderer({ content }: MarkdownRendererProps) {
  return (
    <div className={styles.root}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </div>
  )
}
